import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import useModalStore from "@/store/use-modal-store";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import type { CartChange } from "@/api/models/api-types";

interface ModalContent {
  title: string;
  description: string;
  showConfirm: boolean;
}

export const Modal = () => {
  const { isOpen, type, data, onClose } = useModalStore();
  const { t } = useTranslation();

  const MODAL_TYPES: Record<string, ModalContent> = {
    confirmation: {
      title: t("common.confirmation"),
      description: data?.message || t("common.confirmAction"),
      showConfirm: true,
    },
    deleteChat: {
      title: t("modal.deleteChat"),
      description: t("modal.deleteChatMessage"),
      showConfirm: true,
    },
    unsavedChanges: {
      title: t("modal.unsavedChanges"),
      description: t("modal.unsavedChangesMessage"),
      showConfirm: true,
    },
    logout: {
      title: t("modal.logout"),
      description: t("modal.logoutConfirm"),
      showConfirm: true,
    },
    deleteFiles: {
      title: data?.count === 1 ? t("modal.deleteFile") : t("modal.deleteFiles"),
      description:
        data?.count === 1
          ? t("modal.deleteFileConfirm")
          : t("modal.deleteFilesConfirm", { count: data?.count || 0 }),
      showConfirm: true,
    },
    paymentMethodNotImplemented: {
      title: t("modal.paymentMethodNotImplemented"),
      description: t("modal.paymentMethodNotImplemented"),
      showConfirm: true,
    },
    cancelSongGeneration: {
      title: "Şarkı Üretimini İptal Et",
      description:
        "Şarkı üretimini iptal etmek istediğinizden emin misiniz? İşlem arka planda devam edecek, ancak bildirim artık görüntülenmeyecek.",
      showConfirm: true,
    },
    cartChanges: {
      title: t("modal.cartChangesTitle"),
      description: t("modal.cartChangesDescription"),
      showConfirm: false,
    },
  };

  if (!type) return null;

  const modalContent = MODAL_TYPES[type];

  if (!modalContent) return null;

  // Overlay'e tıklanınca veya ESC'ye basınca çağrılır
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  const handleConfirm = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (data?.onConfirm) {
      data.onConfirm();
    }
    onClose();
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onClose();

    // onCancel callback'i varsa çağır
    if (data?.onCancel) {
      data.onCancel();
    }
  };

  const cartChanges = (data?.changes as CartChange[] | undefined) || [];
  const getReasonLabel = (reason: CartChange["reason"]) => {
    if (reason === "out_of_stock") return t("modal.reasonOutOfStock");
    if (reason === "insufficient_stock")
      return t("modal.reasonInsufficientStock");
    return t("modal.reasonUnavailable");
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn(
          "relative z-50 sm:max-w-[425px] p-6 gap-6",
          "bg-white dark:bg-gray-800",
          "border border-gray-200 dark:border-gray-600 rounded-3xl shadow-lg",
          "overflow-auto max-h-[85vh]",
        )}
        style={{
          // Webkit için kaydırma çubuğu stilleri
          WebkitOverflowScrolling: "touch",
        }}
        open={isOpen}
      >
        <DialogHeader className="relative z-50">
          <DialogTitle className="text-gray-900 dark:text-gray-100">
            {modalContent.title}
          </DialogTitle>
          <DialogDescription className="text-gray-600 dark:text-gray-400 py-2">
            {modalContent.description}
          </DialogDescription>
        </DialogHeader>
        {type === "cartChanges" && (
          <div className="relative z-50 mb-2 max-h-[320px] overflow-auto rounded-xl border border-gray-200 dark:border-gray-700">
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {cartChanges.map((change, index) => (
                <li
                  key={`${change.oldOfferId}-${index}`}
                  className="p-3 text-sm"
                >
                  <p className="font-medium text-gray-900 dark:text-gray-100">
                    {change.productName}
                  </p>
                  <p className="mt-1 text-gray-600 dark:text-gray-300">
                    {change.type === "replaced"
                      ? t("modal.cartChangesReplaced")
                      : t("modal.cartChangesRemoved")}
                  </p>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {getReasonLabel(change.reason)}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        )}
        {modalContent.showConfirm && (
          <DialogFooter className="relative z-50 sm:justify-end gap-2">
            <button
              onClick={handleCancel}
              className="px-3.5 py-2.5 rounded-lg bg-white dark:bg-gray-700 border border-border-primary dark:border-border-primary text-text-secondary dark:text-text-secondary text-sm font-semibold hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("modal.cancel")}
            </button>
            <Button
              variant="darkPrimary"
              onClick={handleConfirm}
              data-dialog-default-action="true"
              autoFocus
              className="relative z-50 text-white hover:bg-primary/90   dark:hover:bg-primary/90"
            >
              {type === "cancelSongGeneration"
                ? "İptal Et"
                : type === "confirmation"
                  ? t("modal.confirm")
                  : type === "logout"
                    ? t("modal.logout")
                    : type === "deleteChat"
                      ? t("modal.delete")
                      : type === "deleteFiles"
                        ? t("modal.delete")
                        : type === "unsavedChanges"
                          ? t("modal.confirm")
                          : type === "paymentMethodNotImplemented"
                            ? t("modal.confirm")
                            : t("modal.delete")}
            </Button>
          </DialogFooter>
        )}
        {type === "cartChanges" && (
          <DialogFooter className="relative z-50 mt-1 border-t border-gray-200 pt-3 dark:border-gray-700 sm:justify-end gap-2">
            <Button
              variant="darkPrimary"
              onClick={handleCancel}
              data-dialog-default-action="true"
              autoFocus
              className="relative z-50 w-full sm:w-auto text-white hover:bg-primary/90 dark:hover:bg-primary/90"
            >
              {t("modal.confirm")}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
